/**
 * Single-flight silent token refresh.
 *
 * With short-lived Keycloak access tokens, a 401 must trigger ONE silent
 * refresh + retry of the original request before we give up and force a
 * logout. Previously every 401 dispatched `auth:unauthorized` immediately,
 * logging users out on every token expiry.
 *
 * CONVENTION (cross-lane): the refresh is performed against the gateway
 * endpoint `POST /api/v1/auth/refresh`, which is part of the anonymous
 * allowlist. The gateway binds `{ refreshToken }` from the JSON body and
 * proxies the OIDC `refresh_token` grant, returning
 * `{ token, refreshToken, expiresIn }` (a freshly minted access token plus a
 * rotated refresh token). The gateway lane owns the endpoint implementation
 * (src/Tyndo.Gateway/Endpoints/AuthEndpoints.cs); the path + shapes are
 * sourced from {@link AUTH_GATEWAY_ENDPOINTS} so a frontend/gateway drift is
 * caught by the auth contract test.
 *
 * The refresh token is persisted at login (see AuthProvider.login) under
 * `auth_refresh_token` and rotated on every successful refresh, because
 * Keycloak refresh-token rotation invalidates the previous one. If the
 * gateway/Keycloak ever owns the refresh entirely (silent iframe / refresh
 * cookie), this module's `setRefresher` hook lets the app swap the strategy
 * without touching callers.
 *
 * All fetch sites (useApi, useApiClient, apiFetch) funnel their 401 handling
 * through `refreshAccessToken()` so concurrent 401s share a single in-flight
 * refresh promise rather than stampeding the endpoint.
 */

import { AUTH_GATEWAY_ENDPOINTS } from './authGatewayContract.ts';
import type { RefreshTokenResponse } from './authGatewayContract.ts';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

const readToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (token) window.localStorage.setItem(AUTH_TOKEN_KEY, token);
    else window.localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

/** Read the persisted refresh token (written at login, rotated on refresh). */
export const readRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Persist (or clear) the refresh token. Called from the login flow with the
 * gateway login response's refresh token, and on logout with `null`. Kept in
 * localStorage alongside `auth_token` so a page reload can still refresh a
 * stale access token without re-prompting for credentials.
 */
export const writeRefreshToken = (refreshToken: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (refreshToken) window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    else window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * A refresher returns a new access token, or null if refresh is impossible
 * (no session / refresh rejected). Replaceable via {@link setRefresher} so an
 * app can plug in a Keycloak-owned strategy.
 */
export type TokenRefresher = (currentToken: string | null) => Promise<string | null>;

/**
 * Default refresher: POSTs the persisted refresh token to the gateway
 * `/auth/refresh` in the JSON body and expects `{ token, refreshToken }` back.
 * The new access token is returned (and persisted by `refreshAccessToken`),
 * and the rotated refresh token is written back here so the next refresh uses
 * the current one. Returns null on any failure.
 */
const defaultRefresher: TokenRefresher = async (currentToken) => {
  if (typeof window === 'undefined') return null;
  // Dev tokens never expire and have no refresh endpoint.
  if (currentToken && currentToken.startsWith('dev-token-')) return null;
  // Without a persisted refresh token the gateway rejects the refresh with a
  // 400 'Missing refresh token' — bail early rather than make a doomed call.
  const refreshToken = readRefreshToken();
  if (!refreshToken) return null;
  // Lazy import to avoid a cycle with apiFetch's URL resolver.
  const { resolveApiBaseUrl } = await import('./apiFetch.ts');
  const baseUrl = resolveApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}${AUTH_GATEWAY_ENDPOINTS.refresh.path}`, {
      method: AUTH_GATEWAY_ENDPOINTS.refresh.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as RefreshTokenResponse | null;
    if (!data?.token) return null;
    // Keycloak rotates refresh tokens: persist the new one so the next refresh
    // doesn't replay an already-consumed (and now-invalid) token.
    if (data.refreshToken) writeRefreshToken(data.refreshToken);
    return data.token;
  } catch {
    return null;
  }
};

let activeRefresher: TokenRefresher = defaultRefresher;

/** Swap the refresh strategy (e.g. a Keycloak-owned silent refresh). */
export const setRefresher = (refresher: TokenRefresher): void => {
  activeRefresher = refresher;
};

let inFlight: Promise<string | null> | null = null;

/**
 * Trigger a single-flight silent refresh. Concurrent callers share one
 * promise. On success the new token is written to localStorage and a
 * `auth:token-refreshed` event is dispatched so the AuthProvider can pick up
 * the new value into React state. Returns the new token, or null on failure.
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (inFlight) return inFlight;
  const current = readToken();
  inFlight = activeRefresher(current)
    .then((next) => {
      if (next) {
        writeToken(next);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('auth:token-refreshed', { detail: { token: next } }),
          );
        }
      }
      return next;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};
